import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Copy, Key, Activity } from 'lucide-react';
import { toast } from 'sonner';

export const UserDashboard = ({ user }) => {
  const copyApiKey = () => {
    navigator.clipboard.writeText(user.api_key);
    toast.success('API Key copied to clipboard');
  };
  
  return (
    <div className="space-y-6" data-testid="user-dashboard-page">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Welcome, {user.name}!</h1>
        <p className="text-gray-600 mt-2">Your API access dashboard</p>
      </div>
      
      {/* API Key Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Key className="h-5 w-5 mr-2" />
            Your API Key
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <code className="text-sm font-mono text-gray-800 break-all">
                {user.api_key}
              </code>
              <Button
                variant="outline"
                size="sm"
                onClick={copyApiKey}
                className="ml-4 flex-shrink-0"
                data-testid="copy-api-key-button"
              >
                <Copy className="h-4 w-4 mr-2" />
                Copy
              </Button>
            </div>
          </div>
          <p className="text-sm text-gray-600 mt-4">
            Use this API key for Bearer token authentication or X-API-Key header
          </p>
        </CardContent>
      </Card>
      
      {/* Quick Stats */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-gray-600">Account Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Status</span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                  Active
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Email</span>
                <span className="text-sm font-medium">{user.email}</span>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-gray-600">Rate Limits</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Per Minute</span>
                <span className="text-sm font-medium">100 requests</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Per Day</span>
                <span className="text-sm font-medium">10,000 requests</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* API Endpoints */}
      <Card>
        <CardHeader>
          <CardTitle>Available Endpoints</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-2">Location Data</h4>
              <div className="space-y-1 text-sm">
                <div className="flex items-center">
                  <span className="text-emerald-600 font-mono mr-2">GET</span>
                  <code className="text-gray-600">/api/v1/province</code>
                </div>
                <div className="flex items-center">
                  <span className="text-emerald-600 font-mono mr-2">GET</span>
                  <code className="text-gray-600">/api/v1/city?province_id=1</code>
                </div>
                <div className="flex items-center">
                  <span className="text-emerald-600 font-mono mr-2">GET</span>
                  <code className="text-gray-600">/api/v1/district?city_id=1</code>
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-2">Shipping Cost</h4>
              <div className="space-y-1 text-sm">
                <div className="flex items-center">
                  <span className="text-blue-600 font-mono mr-2">POST</span>
                  <code className="text-gray-600">/api/v1/cost</code>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Quick Links */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex space-x-4">
            <a href="/api-tester">
              <Button data-testid="go-to-api-tester">
                <Activity className="h-4 w-4 mr-2" />
                Test APIs
              </Button>
            </a>
            <a href="/docs" target="_blank" rel="noopener noreferrer">
              <Button variant="outline">
                View Documentation
              </Button>
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
